import ObexRequestsHandlerP80 from '../handlers/ObexRequestsHandlerP80';
import {
  PurchaseOrder,
  PurchaseOrderLine,
  PurchaseOrderPage,
  PurchaseOrderHeaderPayload,
  PurchaseOrderLinePayload,
  PurchaseStatusHistoryEntry,
  PurchaseOrderStatus
} from '../types/PurchaseOrder.types';
import { getAccessToken, buildQuery, unwrapList, unwrapPage, unwrapRecord, unwrapError, getTransportError } from './apiUtils';

// Los .avap de compras registran todas las rutas con guion bajo. Las de update
// están registradas como PUT pero se llaman con GET igual que /suppliers_update
// (la pasarela acepta los parámetros por query string en cualquier caso).
const ROUTES = {
  list:       '/purchase_orders_list',
  get:        '/purchase_orders_get',
  create:     '/purchase_orders_create',
  update:     '/purchase_orders_update',
  submit:     '/purchase_orders_submit_for_approval',
  approve:    '/purchase_orders_approve',
  reject:     '/purchase_orders_reject',
  cancel:     '/purchase_orders_cancel',
  lineAdd:    '/purchase_order_lines_add',
  lineUpdate: '/purchase_order_lines_update',
  lineRemove: '/purchase_order_lines_remove',
  history:    '/purchase_order_status_history_list'
};

const SESSION_ERROR = 'Sesión no válida o expirada.';

const toId = (value: any): string | null =>
  value === undefined || value === null ? null : String(value);

const toNumber = (value: any, fallback = 0): number => {
  const n = Number(value);
  return isNaN(n) ? fallback : n;
};

export const mapPurchaseOrderLine = (raw: any): PurchaseOrderLine => ({
  id:                String(raw.id),
  purchaseOrderId:   toId(raw.purchase_order_id) || '',
  bookId:            toId(raw.book_id),
  bookTitle:         raw.book_title || '',
  sku:               raw.sku || '',
  description:       raw.description || '',
  supplierReference: raw.supplier_reference || '',
  quantityOrdered:   toNumber(raw.quantity_ordered),
  quantityReceived:  toNumber(raw.quantity_received),
  unitOfMeasure:     raw.unit_of_measure || 'unidades',
  unitPrice:         toNumber(raw.unit_price),
  discountPct:       toNumber(raw.discount_pct),
  taxRate:           toNumber(raw.tax_rate),
  lineTotal:         toNumber(raw.line_total)
});

export const mapStatusHistoryEntry = (raw: any): PurchaseStatusHistoryEntry => ({
  id:            String(raw.id),
  status:        raw.status as PurchaseOrderStatus,
  changedBy:     toId(raw.changed_by),
  changedByName: raw.changed_by_name || '',
  changedAt:     raw.changed_at || '',
  note:          raw.note || ''
});

export const mapPurchaseOrder = (raw: any): PurchaseOrder => ({
  id:                       String(raw.id),
  poNumber:                 raw.po_number || '',
  companyId:                toId(raw.company_id) || '',
  companyName:              raw.company_name || '',
  supplierId:               toId(raw.supplier_id) || '',
  supplierName:             raw.supplier_name || '',
  warehouseId:              toId(raw.warehouse_id) || '',
  warehouseName:            raw.warehouse_name || '',
  costCenterId:             toId(raw.cost_center_id),
  status:                   raw.status as PurchaseOrderStatus,
  createdBy:                toId(raw.created_by),
  createdByName:            raw.created_by_name || '',
  approvedBy:               toId(raw.approved_by),
  approvedByName:           raw.approved_by_name || '',
  issuedAt:                 raw.issued_at || null,
  expectedDeliveryDate:     raw.expected_delivery_date || null,
  alternateDeliveryAddress: raw.alternate_delivery_address || '',
  trackingNumber:           raw.tracking_number || '',
  trackingUrl:              raw.tracking_url || '',
  incoterm:                 raw.incoterm || '',
  shippingMethod:           raw.shipping_method || '',
  carrier:                  raw.carrier || '',
  currency:                 raw.currency || 'EUR',
  exchangeRate:             raw.exchange_rate === null || raw.exchange_rate === undefined ? null : toNumber(raw.exchange_rate),
  paymentMethodSnapshot:    raw.payment_method_snapshot || '',
  paymentTermsSnapshot:     raw.payment_terms_snapshot === null || raw.payment_terms_snapshot === undefined ? '' : String(raw.payment_terms_snapshot),
  bankAccountSnapshot:      raw.bank_account_snapshot || '',
  subtotal:                 toNumber(raw.subtotal),
  taxTotal:                 toNumber(raw.tax_total),
  shippingCost:             toNumber(raw.shipping_cost),
  totalAmount:              toNumber(raw.total_amount),
  notes:                    raw.notes || '',
  createdAt:                raw.created_at || '',
  updatedAt:                raw.updated_at || null,
  lines:                    Array.isArray(raw.lines) ? raw.lines.map(mapPurchaseOrderLine) : [],
  statusHistory:            Array.isArray(raw.status_history) ? raw.status_history.map(mapStatusHistoryEntry) : []
});

const headerQuery = (payload: Partial<PurchaseOrderHeaderPayload>) => ({
  company_id:                 payload.companyId,
  supplier_id:                payload.supplierId,
  warehouse_id:               payload.warehouseId,
  cost_center_id:             payload.costCenterId,
  expected_delivery_date:     payload.expectedDeliveryDate,
  alternate_delivery_address: payload.alternateDeliveryAddress,
  incoterm:                   payload.incoterm,
  shipping_method:            payload.shippingMethod,
  carrier:                    payload.carrier,
  currency:                   payload.currency,
  exchange_rate:              payload.exchangeRate,
  notes:                      payload.notes
});

const lineQuery = (line: Partial<PurchaseOrderLinePayload>) => ({
  book_id:            line.bookId,
  sku:                line.sku,
  description:        line.description,
  supplier_reference: line.supplierReference,
  quantity_ordered:   line.quantityOrdered,
  unit_of_measure:    line.unitOfMeasure,
  unit_price:         line.unitPrice,
  discount_pct:       line.discountPct,
  tax_rate:           line.taxRate
});

/** Acción sobre la cabecera que devuelve la OC (create, update, submit, approve, reject, cancel, lineRemove). */
const requestOrder = async (route: string, params: Record<string, any>, fallback: string): Promise<PurchaseOrder> => {
  const token = getAccessToken();
  if (!token) throw new Error(SESSION_ERROR);

  const res = await ObexRequestsHandlerP80.get(`${route}${buildQuery({ access_token: token, ...params })}`);
  const record = unwrapRecord(res);
  if (!record) throw new Error(unwrapError(res, fallback));
  return mapPurchaseOrder(record);
};

const requestLine = async (route: string, params: Record<string, any>, fallback: string): Promise<PurchaseOrderLine> => {
  const token = getAccessToken();
  if (!token) throw new Error(SESSION_ERROR);

  const res = await ObexRequestsHandlerP80.get(`${route}${buildQuery({ access_token: token, ...params })}`);
  const record = unwrapRecord(res);
  if (!record) throw new Error(unwrapError(res, fallback));
  return mapPurchaseOrderLine(record);
};

export const listPurchaseOrders = async (
  options: {
    status?: string;
    supplierId?: string;
    companyId?: string;
    warehouseId?: string;
    approverId?: string; // solo las OC cuyo solicitante depende de este supervisor (bandeja de aprobación)
    page?: number;
    pageSize?: number;
  } = {}
): Promise<PurchaseOrderPage> => {
  const token = getAccessToken();
  if (!token) throw new Error(SESSION_ERROR);

  const query = buildQuery({
    access_token: token,
    status:       options.status,
    supplier_id:  options.supplierId,
    company_id:   options.companyId,
    warehouse_id: options.warehouseId,
    approver_id:  options.approverId,
    page:         options.page || 1,
    page_size:    options.pageSize || 50
  });

  const res = await ObexRequestsHandlerP80.get(`${ROUTES.list}${query}`);
  const transportError = getTransportError(res);
  if (transportError) throw new Error(`No se pudo cargar el listado de órdenes de compra: ${transportError}`);
  const page = unwrapPage(res);
  return {
    data:     page.rows.map(mapPurchaseOrder),
    total:    page.total,
    page:     page.page,
    pageSize: page.pageSize
  };
};

/** Cabecera + líneas + historial de estados. */
export const getPurchaseOrder = (id: string): Promise<PurchaseOrder> =>
  requestOrder(ROUTES.get, { id }, 'Orden de compra no encontrada.');

export const createPurchaseOrder = (payload: PurchaseOrderHeaderPayload): Promise<PurchaseOrder> =>
  requestOrder(ROUTES.create, headerQuery(payload), 'Error al crear la orden de compra.');

// purchase_orders/update.avap solo toca los campos que llegan != None, así que
// buildQuery omitiendo vacíos da la semántica de update parcial. Solo en borrador.
export const updatePurchaseOrder = (id: string, payload: Partial<PurchaseOrderHeaderPayload>): Promise<PurchaseOrder> =>
  requestOrder(ROUTES.update, { id, ...headerQuery(payload) }, 'Error al modificar la orden de compra.');

export const submitPurchaseOrderForApproval = (id: string): Promise<PurchaseOrder> =>
  requestOrder(ROUTES.submit, { id }, 'Error al enviar la orden a aprobación.');

export const approvePurchaseOrder = (id: string): Promise<PurchaseOrder> =>
  requestOrder(ROUTES.approve, { id }, 'Error al aprobar la orden de compra.');

export const rejectPurchaseOrder = (id: string, reason: string): Promise<PurchaseOrder> =>
  requestOrder(ROUTES.reject, { id, reason }, 'Error al rechazar la orden de compra.');

export const cancelPurchaseOrder = (id: string): Promise<PurchaseOrder> =>
  requestOrder(ROUTES.cancel, { id }, 'Error al cancelar la orden de compra.');

export const addPurchaseOrderLine = (purchaseOrderId: string, line: PurchaseOrderLinePayload): Promise<PurchaseOrderLine> =>
  requestLine(ROUTES.lineAdd, { purchase_order_id: purchaseOrderId, ...lineQuery(line) }, 'Error al añadir la línea.');

export const updatePurchaseOrderLine = (lineId: string, line: Partial<PurchaseOrderLinePayload>): Promise<PurchaseOrderLine> =>
  requestLine(ROUTES.lineUpdate, { id: lineId, ...lineQuery(line) }, 'Error al modificar la línea.');

/** El backend devuelve la OC padre con los totales ya recalculados. */
export const removePurchaseOrderLine = (lineId: string): Promise<PurchaseOrder> =>
  requestOrder(ROUTES.lineRemove, { id: lineId }, 'Error al eliminar la línea.');

export const listPurchaseOrderHistory = async (purchaseOrderId: string): Promise<PurchaseStatusHistoryEntry[]> => {
  const token = getAccessToken();
  if (!token) throw new Error(SESSION_ERROR);

  const res = await ObexRequestsHandlerP80.get(
    `${ROUTES.history}${buildQuery({ access_token: token, purchase_order_id: purchaseOrderId })}`
  );
  return unwrapList(res).map(mapStatusHistoryEntry);
};

export interface FailedLine {
  index: number;
  error: string;
}

/**
 * Alta completa: cabecera y después las líneas, una a una (el backend exige
 * que la OC exista y recalcula los totales en cada add). No hay transacción:
 * si una línea falla, la OC queda en borrador con las que sí entraron y se
 * devuelven los fallos para que la vista avise y deje corregir.
 */
export const createPurchaseOrderWithLines = async (
  header: PurchaseOrderHeaderPayload,
  lines: PurchaseOrderLinePayload[]
): Promise<{ order: PurchaseOrder; failedLines: FailedLine[] }> => {
  const created = await createPurchaseOrder(header);
  const failedLines: FailedLine[] = [];

  for (let i = 0; i < lines.length; i++) {
    try {
      await addPurchaseOrderLine(created.id, lines[i]);
    } catch (err) {
      failedLines.push({ index: i, error: err instanceof Error ? err.message : 'Error desconocido' });
    }
  }

  const order = await getPurchaseOrder(created.id);
  return { order, failedLines };
};

// --- Cálculo en vivo. Mismas fórmulas que purchase_order_lines/add.avap (línea 148 y 167).

const round2 = (n: number): number => Math.round(n * 100) / 100;

export const computeLineTotal = (quantity: number, unitPrice: number, discountPct: number): number =>
  round2(quantity * unitPrice * (1 - discountPct / 100));

export const computeOrderTotals = (
  lines: { lineTotal: number; taxRate: number }[],
  shippingCost = 0
): { subtotal: number; taxTotal: number; totalAmount: number } => {
  const subtotal = round2(lines.reduce((acc, l) => acc + l.lineTotal, 0));
  const taxTotal = round2(lines.reduce((acc, l) => acc + l.lineTotal * l.taxRate / 100, 0));
  return { subtotal, taxTotal, totalAmount: round2(subtotal + taxTotal + shippingCost) };
};
